<template>
  <Modal
    :model-value="modelValue"
    :show-close="false"
    :backdrop-dismiss="false"
    container-class="modal-custom-spacing"
    @update:model-value="onCancel"
  >
    <template #header>Which Connect Group are you in?</template>

    <p class="mb-4 font-inter text-[14px] leading-[1.5] text-grey-70">
      We just need your CG before unlocking this lesson.
    </p>

    <input
      ref="cgInput"
      v-model="cgName"
      type="text"
      inputmode="text"
      autocapitalize="characters"
      autocomplete="off"
      placeholder="Enter your CG"
      aria-label="Connect Group"
      :disabled="submitting"
      @keyup.enter="onSubmit"
      @input="errorMessage = null"
    />

    <p v-if="errorMessage" class="mt-2 font-inter text-[13px] leading-[1.4] text-error">
      {{ errorMessage }}
    </p>

    <template #footer>
      <button
        type="button"
        class="link-cg-btn link-cg-btn--ghost"
        :disabled="submitting"
        @click="onCancel"
      >
        Cancel
      </button>
      <button
        type="button"
        class="link-cg-btn link-cg-btn--primary"
        :disabled="!canSubmit"
        @click="onSubmit"
      >
        {{ submitting ? 'Checking…' : 'Continue' }}
      </button>
    </template>
  </Modal>
</template>

<script>
import { mapState } from 'vuex';
import Modal from '@/components/common/Modal.vue';
import { CG_DOES_NOT_EXIST, isKnownCgName } from '@/utils/cg-status';

/**
 * Prompts for the member's Connect Group and links it before an action that
 * requires one (today: unlocking a lesson from a class QR code).
 *
 * Validation runs twice on purpose. The typed name is checked against the CG
 * directory map (`GET /v2/profile/cg-validation`) to keep the button honest
 * without a round trip — the same normalise-then-lookup used by registration
 * and the profile CG-update card — and `POST /courses/link-cg` re-validates
 * against the live directory, because the client is never the authority on
 * what counts as a real CG.
 *
 * Emits `linked` on success and `cancel` if the member backs out.
 */
export default {
  name: 'LinkCgModal',
  components: { Modal },
  props: {
    modelValue: {
      type: Boolean,
      required: true,
    },
  },
  emits: ['update:modelValue', 'linked', 'cancel'],
  data() {
    return {
      cgName: '',
      errorMessage: null,
      submitting: false,
    };
  },
  computed: {
    ...mapState('registration', ['cgData']),
    canSubmit() {
      return !this.submitting && this.cgName.trim().length > 0;
    },
  },
  watch: {
    modelValue(open) {
      if (!open) return;
      this.cgName = '';
      this.errorMessage = null;
      this.submitting = false;
      // The directory backs the local check; fetchCgData swallows its own
      // errors, leaving the map empty, which isKnownCgName treats as
      // "let the server decide" rather than rejecting everything.
      this.$store.dispatch('registration/fetchCgData');
    },
  },
  methods: {
    async onSubmit() {
      if (!this.canSubmit) return;

      const typed = this.cgName.trim();
      if (!isKnownCgName(typed, this.cgData)) {
        // isKnownCgName now refuses placeholder names too ("None" is a real,
        // active row in the directory), so this branch covers both "no such CG"
        // and "that is the no-CG placeholder" — one message for both, matching
        // what the API returns.
        this.errorMessage = CG_DOES_NOT_EXIST;
        return;
      }

      this.submitting = true;
      this.errorMessage = null;

      try {
        await this.$store.dispatch('courses/linkMainCg', typed);
        // link-cg writes the member's primary CG row; the profile read derives
        // cgName, zone, service and demography from it, so refresh before any
        // caller re-checks the CG.
        await this.$store.dispatch('profile/forceFetchProfile');
        this.$emit('linked', typed);
        this.$emit('update:modelValue', false);
      } catch (error) {
        this.errorMessage =
          error?.response?.data?.message || 'Could not save your CG. Please try again.';
      } finally {
        this.submitting = false;
      }
    },
    onCancel() {
      if (this.submitting) return;
      this.$emit('cancel');
      this.$emit('update:modelValue', false);
    },
  },
};
</script>

<style scoped>
.link-cg-btn {
  border-radius: 9999px;
  padding: 12px 24px;
  font-family: 'Inter', sans-serif;
  font-size: 15px;
  font-weight: 600;
  line-height: 1;
  transition: opacity 0.15s ease;
}

.link-cg-btn:disabled {
  opacity: 0.4;
}

.link-cg-btn--ghost {
  background: transparent;
  color: #6b7280;
}

.link-cg-btn--primary {
  background: #2b2926;
  color: #ffffff;
}
</style>
